export const VIDEO_MIN_WIDTH_PERCENT = 40;
export const VIDEO_MAX_WIDTH_PERCENT = 100;
export const VIDEO_DEFAULT_WIDTH_PERCENT = 80;

export const VIDEO_ALIGN = {
  LEFT: "left",
  CENTER: "center",
  RIGHT: "right",
} as const;

export type VideoAlign = (typeof VIDEO_ALIGN)[keyof typeof VIDEO_ALIGN];

export const VIDEO_DEFAULT_ALIGN: VideoAlign = VIDEO_ALIGN.LEFT;

const VIDEO_WIDTH_HASH_RE = /hv-width=(\d{1,3})/;
const VIDEO_ALIGN_HASH_RE = /hv-align=(left|center|right)/i;
const VIDEO_TRANSCRIPT_HASH_RE = /(?:^|[&#])hv-transcript=1(?:&|$)/;

export type VideoDisplayHash = {
  readonly width_percent: number;
  readonly align: VideoAlign;
  readonly has_transcript: boolean;
};

/** Clamps persisted video widths to a responsive percentage range. */
export function normalizeVideoWidth(value: unknown): number {
  const parsed =
    typeof value === "number"
      ? value
      : typeof value === "string" && value.trim() !== ""
        ? Number(value.trim())
        : Number.NaN;
  if (!Number.isFinite(parsed)) return VIDEO_DEFAULT_WIDTH_PERCENT;
  return Math.max(
    VIDEO_MIN_WIDTH_PERCENT,
    Math.min(VIDEO_MAX_WIDTH_PERCENT, Math.round(parsed)),
  );
}

/** Returns a supported horizontal alignment or the default (left). */
export function normalizeVideoAlign(value: unknown): VideoAlign {
  if (value === VIDEO_ALIGN.CENTER) return VIDEO_ALIGN.CENTER;
  if (value === VIDEO_ALIGN.RIGHT) return VIDEO_ALIGN.RIGHT;
  return VIDEO_ALIGN.LEFT;
}

/** Reads display flags from a `#hv-width=…` hash (transcript only when `=1`). */
export function parseVideoDisplayHash(hash: string): VideoDisplayHash {
  return {
    width_percent: normalizeVideoWidth(VIDEO_WIDTH_HASH_RE.exec(hash)?.[1]),
    align: normalizeVideoAlign(VIDEO_ALIGN_HASH_RE.exec(hash)?.[1]),
    has_transcript: VIDEO_TRANSCRIPT_HASH_RE.test(hash),
  };
}

/** Builds `#hv-width=…`; `hv-transcript=1` only when confirmed. */
export function buildVideoDisplayHash(
  widthPercent: number,
  align: VideoAlign,
  hasTranscript = false,
): string {
  const width = normalizeVideoWidth(widthPercent);
  const normalizedAlign = normalizeVideoAlign(align);
  const alignSuffix =
    normalizedAlign !== VIDEO_DEFAULT_ALIGN
      ? `&hv-align=${normalizedAlign}`
      : "";
  const transcriptSuffix = hasTranscript ? "&hv-transcript=1" : "";
  return `#hv-width=${width}${alignSuffix}${transcriptSuffix}`;
}

/** Spreads `has_transcript: true` only when the flag is set. */
export function transcriptAttr(
  hasTranscript: boolean,
): { readonly has_transcript: true } | Record<string, never> {
  return hasTranscript ? { has_transcript: true } : {};
}
